import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getToken } from '../utils/auth';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function journalLine(line) {
  if (line.debit_amount) return `${line.account_name} ${line.debit_display} Dr`;
  if (line.credit_amount) return `${line.account_name} ${line.credit_display} Cr`;
  return line.account_name;
}

function journalSummary(journal) {
  const lines = (journal.lines || []).map(journalLine).filter(Boolean).join(' · ');
  const reversed = journal.reversed ? 'Reversed · ' : '';
  return `${reversed}${journal.reference_number} ${journal.total_debits_display}${lines ? ` — ${lines}` : ''}`;
}

const ProjectCalculationAuditReport = ({ initialProjectVuid }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [report, setReport] = useState(null);

  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const token = getToken();
        const response = await axios.get(`${baseURL}/api/accounting-periods`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const sorted = [...(response.data || [])].sort((a, b) => {
          if (a.year !== b.year) return b.year - a.year;
          return b.month - a.month;
        });
        setAccountingPeriods(sorted);
        const open = sorted.find((period) => period.status === 'open');
        if (open) setSelectedPeriod(open.vuid);
      } catch (err) {
        setError('Failed to fetch accounting periods');
      }
    };
    loadPeriods();
  }, []);

  useEffect(() => {
    if (!selectedPeriod) {
      setReport(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = getToken();
        const response = await axios.get(`${baseURL}/api/project-calculation-audit`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            accounting_period_vuid: selectedPeriod,
            project_vuid: initialProjectVuid || undefined,
          },
        });
        if (!cancelled) setReport(response.data);
      } catch (err) {
        if (!cancelled) {
          setReport(null);
          setError(err.response?.data?.error || 'Failed to load the Project Calculations audit');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedPeriod, initialProjectVuid]);

  const projects = report?.projects || [];

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-container, .print-container * { visibility: visible; }
          .print-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 0;
          }
          .no-print { display: none !important; }
          .calc-audit-table { font-size: 9px; }
          .calc-audit-table th, .calc-audit-table td {
            padding: 2px 4px;
          }
          .calc-audit-table tr { break-inside: avoid; }
          .calc-audit-table thead { display: table-header-group; }
          @page { size: landscape; margin: 0.4in; }
        }
      `}</style>
      <div className="container mx-auto px-4 py-4 print-container">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Project Calculations Audit</h1>
            {report?.accounting_period ? (
              <p className="text-sm text-gray-600">
                {`${report.accounting_period.label} · ${report.project_count} project${report.project_count === 1 ? '' : 's'} · ${report.missing_count} not posted`}
              </p>
            ) : (
              <p className="text-sm text-gray-600 no-print">
                Enabled calculations, journals posted for the period, and calculations still waiting on a journal.
              </p>
            )}
          </div>
          <div className="flex items-end gap-2 no-print">
            <label className="text-sm" htmlFor="calculation-audit-period">
              <span className="block text-xs font-medium text-gray-600 mb-1">Accounting Period</span>
              <select
                id="calculation-audit-period"
                value={selectedPeriod}
                onChange={(event) => setSelectedPeriod(event.target.value)}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm"
              >
                <option value="">Select an accounting period...</option>
                {accountingPeriods.map((period) => (
                  <option key={period.vuid} value={period.vuid}>
                    {period.month}/{period.year}{period.description ? ` - ${period.description}` : ''} ({period.status})
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="btn-claret" onClick={() => window.print()} disabled={!report}>
              Print
            </button>
          </div>
        </div>

        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-3 text-sm text-red-800 no-print">{error}</div>
        ) : null}

        {loading ? (
          <div className="py-6 text-sm text-gray-600 no-print">Loading Project Calculations audit...</div>
        ) : null}

        {!loading && report && projects.length === 0 ? (
          <div className="text-sm text-gray-600">No projects have an enabled calculation for this period.</div>
        ) : null}

        {!loading && projects.length ? (
          <table className="calc-audit-table w-full text-xs border-collapse bg-white">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="border border-gray-300 px-2 py-1 font-semibold">Project</th>
                <th className="border border-gray-300 px-2 py-1 font-semibold">Calculation</th>
                <th className="border border-gray-300 px-2 py-1 font-semibold">Status</th>
                <th className="border border-gray-300 px-2 py-1 font-semibold">Posting Behavior</th>
                <th className="border border-gray-300 px-2 py-1 font-semibold text-right">Amount</th>
                <th className="border border-gray-300 px-2 py-1 font-semibold">Journal</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => project.calculations.map((calculation, index) => (
                <tr
                  key={calculation.assignment_vuid}
                  className={calculation.journals_outstanding ? 'bg-amber-50' : undefined}
                >
                  {index === 0 ? (
                    <td className="border border-gray-300 px-2 py-1 align-top font-medium" rowSpan={project.calculations.length}>
                      {project.project_number ? `${project.project_number} ` : ''}{project.project_name}
                    </td>
                  ) : null}
                  <td className="border border-gray-300 px-2 py-1 align-top">{calculation.definition_name}</td>
                  <td className="border border-gray-300 px-2 py-1 align-top">{calculation.label}</td>
                  <td className="border border-gray-300 px-2 py-1 align-top whitespace-nowrap">
                    {calculation.job_cost_label || (calculation.job_costed ? 'Create Project Cost' : 'Journal Only')}
                  </td>
                  <td className="border border-gray-300 px-2 py-1 align-top text-right whitespace-nowrap">{calculation.amount_display}</td>
                  <td className="border border-gray-300 px-2 py-1 align-top">
                    {calculation.journals.map((journal) => (
                      <div key={journal.vuid}>{journalSummary(journal)}</div>
                    ))}
                    {calculation.journals_outstanding ? <div>{calculation.message}</div> : null}
                    {!calculation.journals.length && !calculation.journals_outstanding ? (
                      <span>{calculation.status === 'nothing_to_post' ? 'Nothing to post.' : (calculation.job_costed ? 'No project charge in this period.' : 'No journal in this period.')}</span>
                    ) : null}
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        ) : null}
      </div>
    </>
  );
};

export default ProjectCalculationAuditReport;
